import React, { Component, ReactNode } from 'react'
import { Address4, Address6 } from 'ip-address'
import classnames from 'classnames'
import { isEqual } from 'lodash'
import { v1 as uuidv1 } from 'uuid'
import { Trans } from '@lingui/macro'

import Label from '../../../../components/common/form/label/Label'
import Input from '../../../../components/common/form/input/Input'
import { Checkbox } from '../../../../components/common/form/checkbox/Checkbox'
import { ButtonPrimary } from 'front-end/src/components'
import IconTrash from '../../../../components/common/icon/IconTrash'
import IconButton from '../../../../components/common/form/button/IconButton'
import IPut from './iput/IPut'
import {
  organisationWhitelistRecordToType,
  OrganisationIPWhitelist,
  IOrganisationIPWhitelistRange,
  IOrganisationIPWhitelistSingle,
  OrganisationIPWhitelistRecord,
  OrganisationIPWhitelistTypeEnum
} from '../../../../../../types/Organisation'

import './IpWhitelist.less'

enum GuessedIPInputType {
  None,
  IPv4,
  IPv6
}

interface IProps {
  whitelist: OrganisationIPWhitelist
  onUpdateWhitelist: (whitelists: OrganisationIPWhitelist) => Promise<void>
}

interface IState {
  dirty: boolean
  loading: boolean
  enabled: boolean
  valid: boolean
  localWhitelist: IPWhitelistWrappedRecord[]
}

interface IPWhitelistWrappedRecord {
  tmpId: string
  record: OrganisationIPWhitelistRecord
}

interface IPType {
  component: any
  isValidFunc: (ip: string) => boolean
}

interface IPRecordType {
  isValidFunc: (record: OrganisationIPWhitelistRecord) => boolean
  render: (
    record: OrganisationIPWhitelistRecord,
    index: number,
    tmpId: string
  ) => ReactNode
}

const guessInputTypeFromRecord = (ip: string): GuessedIPInputType => {
  if (ip === '0.0.0.0') {
    return GuessedIPInputType.None
  }

  return ip.match(/\d{1,3}\.(.*)/)
    ? GuessedIPInputType.IPv4
    : GuessedIPInputType.IPv6
}

const getIpTypeConfig = (ipType: GuessedIPInputType): IPType => {
  switch (ipType) {
    case GuessedIPInputType.IPv4:
      return {
        component: IPut,
        isValidFunc: (ip: string) => new Address4(ip).isValid()
      }

    case GuessedIPInputType.IPv6:
      return {
        component: Input,
        isValidFunc: (ip: string) => new Address6(ip).isValid()
      }

    default:
      return {
        component: Input,
        isValidFunc: () => false
      }
  }
}

const getIpTypeConfigFromIp = (ip: string) =>
  getIpTypeConfig(guessInputTypeFromRecord(ip))

const wrapWhitelistRecord = (
  record: OrganisationIPWhitelistRecord
): IPWhitelistWrappedRecord => ({
  tmpId: uuidv1(),
  record
})

// TODO rewrite to functional component with hooks
export default class IpWhitelist extends Component<IProps, IState> {
  state = {
    dirty: false,
    loading: false,
    enabled: false,
    valid: true,
    localWhitelist: [] as IPWhitelistWrappedRecord[]
  }

  componentDidMount(): void {
    this.initWhitelist(this.props.whitelist)
  }

  componentDidUpdate(prevProps: IProps): void {
    const { whitelist } = this.props

    if (prevProps.whitelist !== whitelist) {
      this.initWhitelist(whitelist)
    }
  }

  handleToggleEnabled = (enabled: boolean): void => {
    const { localWhitelist } = this.state

    this.setState({
      enabled,
      dirty: enabled ? this.isDirty(localWhitelist) : true,
      valid: this.isWhitelistValid(localWhitelist)
    })
  }

  handleLocalFormChange = (
    index: number,
    record: OrganisationIPWhitelistRecord
  ): void => {
    const { localWhitelist } = this.state

    const clonedWhitelist: IPWhitelistWrappedRecord[] = localWhitelist
      ? [...localWhitelist]
      : []
    clonedWhitelist[index] = { ...clonedWhitelist[index], record }

    this.setState({
      localWhitelist: clonedWhitelist,
      dirty: this.isDirty(clonedWhitelist),
      valid: this.isWhitelistValid(clonedWhitelist)
    })
  }

  handleUpdateButtonClick = async (): Promise<void> => {
    const { enabled, localWhitelist } = this.state

    this.setState({
      loading: true
    })

    try {
      const unwrappedWhitelist = localWhitelist.map((wrapped) => wrapped.record)
      await this.props.onUpdateWhitelist(enabled ? unwrappedWhitelist : [])

      this.setState({
        dirty: false
      })
    } finally {
      this.setState({
        loading: false
      })
    }
  }

  handleAddIPClick = (): void => {
    const { localWhitelist } = this.state

    this.setState({
      localWhitelist: [
        ...localWhitelist,
        wrapWhitelistRecord({
          ip: ''
        })
      ]
    })
  }

  handleAddIPRangeClick = (): void => {
    const { localWhitelist } = this.state

    this.setState({
      localWhitelist: [
        ...localWhitelist,
        wrapWhitelistRecord({
          from: '',
          to: ''
        })
      ]
    })
  }

  render(): ReactNode {
    const { enabled, valid } = this.state

    return (
      <div className={classnames('IpWhitelist', { valid })}>
        <div className="AccountPage__checkBoxWrapper">
          <Checkbox
            id="IpWhitelist__email_comments"
            isChecked={enabled}
            onChange={(event) =>
              this.handleToggleEnabled((event.target as any).checked)
            }
          >
            <Trans>Use IP allowlist</Trans>
          </Checkbox>
        </div>
        {this.renderEnabledOptions()}
        {this.renderSaveButton()}
      </div>
    )
  }

  private renderEnabledOptions(): ReactNode {
    if (!this.state.enabled) {
      return
    }

    return (
      <>
        {this.renderWhitelist()}
        {this.renderAddButtons()}
      </>
    )
  }

  private renderWhitelist(): ReactNode {
    const { localWhitelist } = this.state

    return localWhitelist.map(({ record, tmpId }, index) =>
      this.renderWhitelistInput(record, index, tmpId)
    )
  }

  private renderSaveButton(): ReactNode {
    const { loading, dirty, valid } = this.state

    if (!dirty) {
      return
    }

    return (
      <ButtonPrimary
        mt="6px"
        isLoading={loading}
        onClick={this.handleUpdateButtonClick}
        disabled={!valid}
      >
        <Trans>Update allowlist</Trans>
      </ButtonPrimary>
    )
  }

  private renderWhitelistInput(
    record: OrganisationIPWhitelistRecord,
    index: number,
    tmpId: string
  ): ReactNode {
    return this.getIpRecordTypeFromRecord(record).render(record, index, tmpId)
  }

  private renderSingleIpInput(
    record: IOrganisationIPWhitelistSingle,
    index: number,
    tmpId: string
  ): ReactNode {
    const { component: InputComponent } = getIpTypeConfigFromIp(record.ip)

    return (
      <div className="IpWhitelist__record" key={tmpId}>
        <div className="prefixed-label">
          <Label>
            <Trans>IP</Trans>
          </Label>
          <InputComponent
            className="Input"
            value={record.ip}
            defaultValue={record.ip}
            onChange={(ip) => {
              this.handleLocalFormChange(index, { ip })
            }}
          />
        </div>
        {this.renderRemoveIcon(index)}
      </div>
    )
  }

  private renderRangeInputs(
    range: IOrganisationIPWhitelistRange,
    index: number,
    tmpId: string
  ): ReactNode {
    const { component: InputComponent } = getIpTypeConfigFromIp(range.from)

    return (
      <div className="IpWhitelist__record" key={tmpId}>
        <div className="prefixed-label">
          <Label>
            <Trans>From</Trans>
          </Label>
          <InputComponent
            className="Input"
            value={range.from}
            defaultValue={range.from}
            onChange={(from) => {
              this.handleLocalFormChange(index, { ...range, from })
            }}
          />
        </div>
        <div className="prefixed-label">
          <Label>
            <Trans>To</Trans>
          </Label>
          <InputComponent
            className="Input"
            value={range.to}
            defaultValue={range.to}
            onChange={(to) => {
              this.handleLocalFormChange(index, { ...range, to })
            }}
          />
        </div>
        {this.renderRemoveIcon(index)}
      </div>
    )
  }

  private renderAddButtons(): ReactNode {
    return (
      <div>
        <ButtonPrimary onClick={this.handleAddIPClick}>
          <Trans>Add IP</Trans>
        </ButtonPrimary>
        <ButtonPrimary onClick={this.handleAddIPRangeClick}>
          <Trans>Add IP range</Trans>
        </ButtonPrimary>
      </div>
    )
  }

  private renderRemoveIcon(index: number): ReactNode {
    return (
      <IconButton
        icon={<IconTrash />}
        onClick={() => this.removeWhitelistRecord(index)}
      />
    )
  }

  private initWhitelist(whitelist: OrganisationIPWhitelist): void {
    if (whitelist) {
      const whitelistWithIds = whitelist.map(wrapWhitelistRecord)

      this.setState({
        localWhitelist: whitelistWithIds,
        enabled: whitelist.length > 0
      })
    } else {
      this.setState({
        localWhitelist: [],
        enabled: false
      })
    }

    this.setState({
      dirty: false
    })
  }

  private removeWhitelistRecord(index: number): void {
    const { localWhitelist } = this.state
    const newWhitelist = [...localWhitelist]
    newWhitelist.splice(index, 1)

    this.setState({
      localWhitelist: newWhitelist,
      dirty: this.isDirty(newWhitelist)
    })
  }

  private isDirty(records: IPWhitelistWrappedRecord[]): boolean {
    return !isEqual(
      records.map((v) => v.record),
      this.props.whitelist
    )
  }

  private isWhitelistValid = (wrapped: IPWhitelistWrappedRecord[]): boolean => {
    return wrapped.every(({ record }) =>
      this.getIpRecordTypeFromRecord(record).isValidFunc(record)
    )
  }

  private getIpRecordTypeFromRecord(
    record: OrganisationIPWhitelistRecord
  ): IPRecordType {
    switch (organisationWhitelistRecordToType(record)) {
      case OrganisationIPWhitelistTypeEnum.Single:
        return {
          // @ts-expect-error 9/11/2020
          render: (
            r: IOrganisationIPWhitelistSingle,
            index: number,
            tmpId: string
          ) => this.renderSingleIpInput(r, index, tmpId),
          // @ts-expect-error 9/11/2020
          isValidFunc: (r: IOrganisationIPWhitelistSingle) =>
            getIpTypeConfigFromIp(r.ip).isValidFunc(r.ip)
        }

      case OrganisationIPWhitelistTypeEnum.Range:
        return {
          // @ts-expect-error 9/11/2020
          render: (
            r: IOrganisationIPWhitelistRange,
            index: number,
            tmpId: string
          ) => this.renderRangeInputs(r, index, tmpId),
          // @ts-expect-error 9/11/2020
          isValidFunc: (r: IOrganisationIPWhitelistRange) =>
            getIpTypeConfigFromIp(r.from).isValidFunc(r.from)
        }
    }
  }
}
